import PropTypes from "prop-types";
import {useEffect, useState} from "react";
import {getAll as getTags} from "app/requests/admin/tags";
import Checkbox from "components/helpers/form/checkbox";
import ContentEditor from "components/helpers/form/content-editor";
import MultiSelectData from "components/helpers/form/multi-select-data";
import Notification from "components/helpers/notification";
import Loading from "components/pages/loading";
import {valueFromTarget} from "lib/form";

function Question({index, onChange: setChange, record}) {
  const id = `record-question-${index}`;
  const onChange = ({target}) => {
    setChange({index, record: {...record, [target.name]: valueFromTarget(target)}});
  };

  return (
    <div key={record.rank}>
      <div className="mb-4">
        <label htmlFor={`${id}-content-disabled`}>Question {index + 1}</label>
        <ContentEditor
          id={`${id}-content`}
          name="content"
          onChange={onChange}
          value={record.content || ""}
        />
      </div>
      <div className="mb-4">
        <label htmlFor={`${id}-answer-disabled`}>Answer {index + 1}</label>
        <ContentEditor
          id={`${id}-answer`}
          name="answer"
          onChange={onChange}
          value={record.answer || ""}
        />
      </div>
    </div>
  );
}

Question.propTypes = {
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  record: PropTypes.shape({
    answer: PropTypes.string,
    content: PropTypes.string,
    rank: PropTypes.number.isRequired
  }).isRequired
};

function AdminCasesForm({onChange, onSubmit, value}) {
  const [tags, setTags] = useState(null);

  useEffect(() => {
    getTags().then((data) => setTags(data.tags));
  }, []);

  if(!tags) { return <Loading />; }

  const addQuestion = () => {
    const questions = [...value("questions")];
    questions[questions.length] = {rank: questions.length};
    onChange({target: {name: "questions", value: questions}});
  };
  const onQuestionChange = ({index, record}) => {
    const questions = [...value("questions")];
    questions[index] = record;
    onChange({target: {name: "questions", value: questions}});
  };
  const options = tags.map((tag) => ({
    name: `${tag.category.name} - ${tag.name}`,
    value: tag.id.toString()
  }));

  return (
    <div className="max-w-sm mx-auto">
      <form onSubmit={onSubmit}>
        <div className="mb-4">
          <Checkbox
            checked={value("active", false)}
            id="record-active"
            label="Active"
            name="active"
            onChange={onChange}
          />
        </div>
        <div className="mb-4">
          <label htmlFor="record-name">Name</label>
          <input
            id="record-name"
            name="name"
            onChange={onChange}
            placeholder="Rectum? Darn near killed him!"
            required={true}
            type="text"
            value={value("name")}
          />
        </div>
        <div className="mb-4">
          <label htmlFor="record-number">Number</label>
          <input
            id="record-number"
            name="number"
            onChange={onChange}
            placeholder="Defaults to the next highest number"
            type="number"
            value={value("number")}
          />
        </div>
        <div className="mb-4">
          <label htmlFor="record-tag-ids">Tags</label>
          {tags.length === 0 ? (
            <Notification type="error">There are no tags available. You can create tags <a href="/admin/tags">here</a>.</Notification>
          ) : (
            <MultiSelectData
              fields={[
                {name: "number", placeholder: "Number (optional)", type: "number"},
                {name: "name", placeholder: "Name (optional)", type: "text"}
              ]}
              id="record-case-tags"
              name="case_tags"
              onChange={onChange}
              select={{name: "tag_id", options, placeholder: "Select Tags"}}
              value={value("case_tags") || []}
            />
          )}
        </div>
        <div className="mb-4">
          <label htmlFor="record-content-disabled">Content</label>
          <ContentEditor
            id="record-content"
            name="content"
            onChange={onChange}
            value={value("content")}
          />
        </div>
        {value("questions").map((question, index) => (
          <Question
            key={index} /* eslint-disable-line react/no-array-index-key */
            index={index}
            onChange={onQuestionChange}
            record={question}
          />
        ))}
        <div className="mb-4 text-center">
          <button className="btn btn-yellow" onClick={addQuestion} type="button">Add Question</button>
        </div>
        <div className="mb-4">
          <label htmlFor="record-deep-dive-disabled">Deep Dive</label>
          <ContentEditor
            id="record-deep-dive"
            name="deep_dive"
            onChange={onChange}
            value={value("deep_dive")}
          />
        </div>
        <div className="mb-4">
          <label htmlFor="record-quick-take-disabled">Quick Take</label>
          <ContentEditor
            id="record-quick-take"
            name="quick_take"
            onChange={onChange}
            value={value("quick_take")}
          />
        </div>
        <div className="text-center">
          <button type="submit" className="btn btn-blue">Submit</button>
        </div>
      </form>
    </div>
  );
}

AdminCasesForm.propTypes = {
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  value: PropTypes.func.isRequired
};

export default AdminCasesForm;
